import { createRoute, useNavigate } from "@tanstack/react-router";
import { withPermission } from "../lib/with-permission";
import { useTranslation } from "react-i18next";
import { useState, useCallback, useEffect } from "react";
import type { SearchableSelectOption } from "../components/ui/SearchableSelect/SearchableSelect";
import { rootRoute } from "./__root";
import { PageHeader } from "../components/ui/PageHeader/PageHeader";
import { Card } from "../components/ui/Card/Card";
import { Button } from "../components/ui/Button/Button";
import { Input } from "../components/ui/Input/Input";
import { FormField } from "../components/ui/FormField/FormField";
import { Select } from "../components/ui/Select/Select";
import { SearchableSelect } from "../components/ui/SearchableSelect/SearchableSelect";
import { useCreateContract } from "../hooks/use-contracts";
import { api } from "../lib/api";
import styles from "./ContractNew.module.css";

export const route = createRoute({
	getParentRoute: () => rootRoute,
	path: "/contracts/new",
	component: withPermission(ContractNewPage, "contracts", "create"),
});

function ContractNewPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const createContract = useCreateContract();
	const search = route.useSearch() as {
		companyId?: string;
		orderId?: string;
	};
	const [form, setForm] = useState({
		title: "",
		type: "sales",
		companyId: search.companyId ?? "",
		orderId: search.orderId ?? "",
		startDate: "",
		endDate: "",
		value: "",
		currency: "TL",
		autoRenewal: false,
		renewalNoticeDays: "",
		terms: "",
		notes: "",
	});
	const [formError, setFormError] = useState("");
	const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
	const [initialOptions, setInitialOptions] = useState<
		Record<string, SearchableSelectOption | undefined>
	>({});

	useEffect(() => {
		const opts: Record<string, SearchableSelectOption> = {};
		const fetches: Promise<void>[] = [];
		if (search.companyId) {
			fetches.push(
				api
					.get<{ id: string; legalName: string }>(`/api/companies/${search.companyId}`)
					.then((c) => {
						opts.company = { value: c.id, label: c.legalName };
					})
					.catch(() => {}),
			);
		}
		if (search.orderId) {
			fetches.push(
				api
					.get<{ id: string; documentNumber: string | null }>(`/api/orders/${search.orderId}`)
					.then((o) => {
						opts.order = { value: o.id, label: o.documentNumber ?? o.id.slice(0, 8) };
					})
					.catch(() => {}),
			);
		}
		Promise.all(fetches).then(() => setInitialOptions(opts));
	}, [search.companyId, search.orderId]);

	const loadCompanies = useCallback(async (query: string) => {
		const res = await api.get<{ items: Array<{ id: string; legalName: string }> }>(
			`/api/companies?limit=50${query ? `&search=${encodeURIComponent(query)}` : ""}`,
		);
		return res.items.map((c) => ({ value: c.id, label: c.legalName }));
	}, []);

	const loadOrders = useCallback(
		async (query: string) => {
			const res = await api.get<{
				items: Array<{ id: string; documentNumber: string | null; companyName: string | null }>;
			}>(
				`/api/orders?limit=50${form.companyId ? `&companyId=${form.companyId}` : ""}${query ? `&search=${encodeURIComponent(query)}` : ""}`,
			);
			return res.items.map((o) => ({
				value: o.id,
				label: `${o.documentNumber || o.id.slice(0, 8)} — ${o.companyName || ""}`,
			}));
		},
		[form.companyId],
	);

	const submit = () => {
		setFormError("");
		const errors: Record<string, string> = {};
		if (!form.title?.trim()) errors.title = t("requiredField");
		if (!form.companyId) errors.companyId = t("requiredField");
		if (!form.startDate) errors.startDate = t("requiredField");
		if (!form.endDate) errors.endDate = t("requiredField");
		setFieldErrors(errors);
		if (Object.keys(errors).length > 0) return;
		createContract.mutate(
			{
				title: form.title,
				type: form.type as "sales" | "maintenance" | "rental" | "service",
				companyId: form.companyId || undefined,
				orderId: form.orderId || undefined,
				startDate: form.startDate || undefined,
				endDate: form.endDate || undefined,
				value: form.value ? Number(form.value) : undefined,
				currency: form.currency || undefined,
				autoRenewal: form.autoRenewal,
				renewalNoticeDays: form.renewalNoticeDays ? Number(form.renewalNoticeDays) : undefined,
				terms: form.terms || undefined,
				notes: form.notes || undefined,
			},
			{
				onSuccess: () => {
					alert(t("createSuccess"));
					navigate({
						to: "/contracts",
						search: {
							page: 1,
							size: 20,
							status: "",
							search: "",
							createdAtFrom: "",
							createdAtTo: "",
						},
					});
				},
				onError: (err) => {
					setFormError(err instanceof Error ? err.message : t("error"));
				},
			},
		);
	};

	return (
		<div>
			<PageHeader title={t("newContract")} />

			<div className={styles.grid}>
				<Card header={t("generalInfo")}>
					<div className={styles.formGrid}>
						{formError && <div className={styles.formError}>{formError}</div>}
						<FormField label={t("title")} required>
							<Input
								onChange={(e) => setForm({ ...form, title: e.target.value })}
								error={fieldErrors.title}
							/>
						</FormField>
						<FormField label={t("type")}>
							<Select
								options={[
									{ value: "sales", label: t("sales") },
									{ value: "maintenance", label: t("maintenance") },
									{ value: "rental", label: t("rental") },
									{ value: "service", label: t("service") },
								]}
								value={form.type}
								onChange={(e) => setForm({ ...form, type: e.target.value })}
							/>
						</FormField>
						<FormField label={t("company")} required>
							<SearchableSelect
								value={form.companyId}
								onChange={(val) => {
									setForm({
										...form,
										companyId: val,
										orderId: "",
									});
									setInitialOptions((prev) => ({
										...prev,
										order: undefined,
									}));
								}}
								loadOptions={loadCompanies}
								initialOption={initialOptions.company}
								placeholder={t("selectCompany")}
								clearable
							/>
						</FormField>
						<FormField label={t("order")}>
							<SearchableSelect
								value={form.orderId}
								onChange={(val) => setForm({ ...form, orderId: val })}
								loadOptions={loadOrders}
								initialOption={initialOptions.order}
								placeholder={t("selectOrder")}
								clearable
								disabled={!form.companyId}
							/>
						</FormField>
						<FormField label={t("startDate")} required>
							<Input
								type="date"
								value={form.startDate}
								onChange={(e) => setForm({ ...form, startDate: e.target.value })}
								error={fieldErrors.startDate}
							/>
						</FormField>
						<FormField label={t("endDate")} required>
							<Input
								type="date"
								value={form.endDate}
								onChange={(e) => setForm({ ...form, endDate: e.target.value })}
								error={fieldErrors.endDate}
							/>
						</FormField>
						<FormField label={t("value")}>
							<Input
								type="number"
								value={form.value}
								onChange={(e) => setForm({ ...form, value: e.target.value })}
							/>
						</FormField>
						<FormField label={t("currency")}>
							<Input
								value={form.currency}
								onChange={(e) => setForm({ ...form, currency: e.target.value })}
							/>
						</FormField>
						<FormField label={t("renewalNoticeDays")}>
							<Input
								type="number"
								value={form.renewalNoticeDays}
								onChange={(e) => setForm({ ...form, renewalNoticeDays: e.target.value })}
							/>
						</FormField>
					</div>
					<div className={styles.checkboxRow}>
						<label className={styles.checkboxLabel}>
							<input
								type="checkbox"
								checked={form.autoRenewal}
								onChange={(e) => setForm({ ...form, autoRenewal: e.target.checked })}
							/>
							<span>{t("autoRenewal")}</span>
						</label>
					</div>
					<FormField label={t("terms")}>
						<Input
							value={form.terms}
							onChange={(e) => setForm({ ...form, terms: e.target.value })}
						/>
					</FormField>
					<FormField label={t("notes")}>
						<Input
							value={form.notes}
							onChange={(e) => setForm({ ...form, notes: e.target.value })}
						/>
					</FormField>
				</Card>

				<Card>
					<div className={styles.submitRow}>
						<Button onClick={submit} isLoading={createContract.isPending}>
							{t("create")}
						</Button>
					</div>
				</Card>
			</div>
		</div>
	);
}
